import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker, Drawer, Select, Input, Modal } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { ButtonWrapper } from 'components';
import { Box, Flex, Text, Button } from 'atoms';
import { FilterTitle, FilterTag, ACL } from 'components/common';
import { getTripFilterList, getListAction } from 'store/pageSlice';
import { getTripFilterListV2 } from 'store/tripSliceV2';
import { getFilterListV2 } from 'store/financeSliceV2';
import { useCascadingTripFilters } from 'hooks';
import { exportAdhocRequests, exportAdhocRequestsV2 } from 'store/advancePaymentSlice';
import { initialPaginateConfig, DATE_FORMAT_WITH_TIME } from 'utils';
import { getUIVersion } from 'utils/router-static-data';
import { FilterAtnButton } from 'utils/commonStyled';
import {
	formatDateForAPI,
	formatEndDateForAPI,
	formatDateForRangePicker,
} from 'utils/datePersistence';

const { RangePicker } = DatePicker;
const { Search } = Input;

const LtSearchBar = styled(Search)`
	border: none;
	width: 100%;
`;

const ResponsiveFilterContainer = styled(Box)`
	@media (max-width: 768px) {
		padding: 16px !important;
	}
`;

const ResponsiveFlexContainer = styled(Flex)`
	gap: 8px;

	@media (max-width: 1200px) {
		flex-wrap: wrap;
		gap: 8px;
	}
`;

const ResponsiveSearchBox = styled(Box)`
	flex: 1 1 auto;
	min-width: 200px;
	max-width: 300px;
	margin-right: 8px;

	@media (max-width: 1200px) {
		flex: 1 1 100%;
		min-width: 100%;
		max-width: 100%;
		margin-right: 0;
		margin-bottom: 8px;
	}
`;

const ResponsiveFiltersBox = styled(Box)`
	flex: 0 0 auto;
	min-width: 200px;
	margin-left: 8px;
	margin-right: 8px;

	@media (max-width: 1200px) {
		flex: 1 1 100%;
		min-width: 100%;
		margin-left: 0;
		margin-right: 0;
		margin-top: 8px;
		order: 3;
	}
`;

const ResponsiveFilterTagsContainer = styled(Box)`
	flex: 1 1 auto;
	min-width: 150px;
	max-width: 250px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;

	@media (max-width: 768px) {
		max-width: calc(100% - 120px);
		min-width: 120px;
		flex: 1 1 auto;
		margin-right: 8px;
	}
`;

const ResponsiveDateBox = styled(Box)`
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: nowrap;
	margin-left: 8px;

	@media (max-width: 1200px) {
		flex: 1 1 100%;
		min-width: 100%;
		margin-left: 0;
		margin-top: 8px;
		order: 2;
	}

	@media (max-width: 768px) {
		flex-wrap: wrap;
		gap: 8px;
	}
`;

const ResponsiveDateLabel = styled(Text)`
	white-space: nowrap;

	@media (max-width: 768px) {
		display: none;
	}
`;

const ResponsiveDatePicker = styled(RangePicker)`
	min-width: 200px;
	flex: 1 1 auto;

	@media (max-width: 768px) {
		min-width: calc(100% - 160px);
		flex: 1 1 auto;
	}
`;

const CustomModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 4px;
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
		padding: 0;
		text-align: center;
		padding-inline: 50px;
	}
	.ant-modal-body {
		padding: 0;
	}
`;

const ModalContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 28px;
	padding: 0px 0px 24px;
`;

const UpperContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
	padding: 20px 0px 0px;
	width: 100%;
`;

const WarningIcon = styled.div`
	svg {
		width: 36px;
		height: 32px;
	}
`;

const TextContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	align-self: stretch;
	gap: 13px;
`;

const TitleContainer = styled.div`
	display: flex;
	width: 470px;
	gap: 24px;
`;

const WarningText = styled.p`
	font-family: Inter, sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 1.25em;
	text-align: center;
	color: #000000;
	margin: 0;
`;

const QuestionContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-self: stretch;
	gap: 8px;
`;

const QuestionText = styled.p`
	font-family: Inter, sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 1.25em;
	text-align: center;
	color: #666666;
	margin: 0;
`;

const ActionButtons = styled.div`
	display: flex;
	gap: 16px;
	padding: 0px 0px 24px;
`;

const NoButton = styled(Button)`
	border: 1px solid #000000;
	color: #000000;
	background-color: #ffffff;
	width: 120px;
	height: 40px;

	&:hover {
		border-color: #000000;
		color: #000000;
		background-color: #f5f5f5;
	}
`;

const YesButton = styled(Button)`
	width: 120px;
	height: 40px;
`;

const allOption = {
	value: '',
	label: 'All',
	key: 'All',
};

function populateOptions(data = [], showAll = true) {
	data = data?.map((element) => ({
		value: element?.id,
		label: element?.value,
	}));

	if (showAll) {
		data.unshift(allOption);
	}

	return data;
}

function AdhocRequestFilters({
	paginationConfig,
	setPaginationConfig,
	filterKey,
	showCreateButton = false,
}) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [rangePickerValue, setRangePickerValue] = useState(null);
	const [searchValue, setSearchValue] = useState('');
	const [exportWarningModal, setExportWarningModal] = useState(false);

	// Filter lists
	const [citiesList, setCitiesList] = useState('');
	const [clientsList, setClientsList] = useState('');
	const [projectsList, setProjectsList] = useState('');
	const [vendorsList, setVendorsList] = useState('');
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [businessUnitFilterValue, setBusinessUnitFilterValue] = useState('');

	// Clear empty or unwanted query_str from pagination config
	useEffect(() => {
		const queryStr = paginationConfig?.query_str;
		// If query_str exists but is empty/whitespace or unwanted value, remove it from config
		if (
			queryStr !== undefined &&
			(!queryStr || !queryStr.trim() || queryStr.trim().toLowerCase() === 'anas')
		) {
			const { query_str, ...restConfig } = paginationConfig;
			setPaginationConfig(restConfig);
			// Also clear the search value
			setSearchValue('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationConfig?.query_str]);

	// Sync search value with pagination config (only if query_str is valid)
	useEffect(() => {
		const queryStr = paginationConfig?.query_str;
		// Only sync if query_str exists, is not empty, and is not an unwanted value
		if (queryStr && queryStr.trim() && queryStr.trim().toLowerCase() !== 'anas') {
			setSearchValue(queryStr.trim());
		} else {
			// Clear search value if query_str is empty, undefined, or unwanted
			setSearchValue('');
		}
	}, [paginationConfig?.query_str]);

	// RIPO-6667: cross-dependent (cascading) options for the V2 UI — faceted over adhoc
	// requests (not trips). Fetched only while the drawer is open; narrows on each change.
	const { options: cascadeOptions } = useCascadingTripFilters({
		paginateConfig: paginationConfig,
		enabled: getUIVersion() === 'new' && open,
		listSearchType: 'adhoc_request',
	});
	useEffect(() => {
		if (getUIVersion() !== 'new') return;
		setCitiesList([allOption, ...(cascadeOptions.cities || [])]);
		setClientsList([allOption, ...(cascadeOptions.clients || [])]);
		setProjectsList([allOption, ...(cascadeOptions.projects || [])]);
		setVendorsList([allOption, ...(cascadeOptions.vendors || [])]);
	}, [cascadeOptions]);

	// Helper function to fetch city filter (always V1)
	const fetchCityFilter = useCallback(() => {
		dispatch(getTripFilterList('city')).then((res) => {
			const { rows = [] } = res;
			const cities = rows?.cities?.map((element) => ({
				value: element,
				label: element,
			}));
			cities.unshift(allOption);
			setCitiesList(cities);
		});
	}, [dispatch]);

	// Helper function to fetch vendor filter (V2 uses list_search to avoid auth issues)
	const fetchVendorFilter = useCallback(() => {
		const isV2UI = getUIVersion() === 'new';

		if (isV2UI) {
			// Use /list_search for vendor to avoid authentication issues
			dispatch(getFilterListV2({ filter: 'vendor' }))
				.then((res) => {
					const rows = res?.rows || [];
					const vendors = Array.isArray(rows)
						? rows.map((element) => ({
								value: element?.id || element?.value,
								label:
									element?.label ||
									element?.name ||
									element?.vendor_name ||
									`Vendor ${element?.id || element?.value}`,
						  }))
						: [];
					vendors.unshift(allOption);
					setVendorsList(vendors);
				})
				.catch((error) => {
					console.error('Error fetching vendor filters:', error);
					setVendorsList([allOption]);
				});
		} else {
			// V1 API for old UI
			dispatch(getTripFilterList('vendor')).then((res) => {
				const { rows = [] } = res;
				const vendors = populateOptions(rows.vendors);
				setVendorsList(vendors);
			});
		}
	}, [dispatch]);

	// Helper function to map V2 client data
	const mapV2ClientToOption = (element) => ({
		value: element?.id || element?.value,
		label:
			element?.client_name ||
			element?.client_legal_name ||
			element?.company_name ||
			element?.label ||
			element?.name ||
			`Client ${element?.id || element?.value}`,
	});

	// Helper function to map V2 project data
	const mapV2ProjectToOption = (element) => ({
		value: element?.id || element?.value,
		label:
			element?.project_name ||
			element?.project_code ||
			element?.label ||
			element?.name ||
			`Project ${element?.id || element?.value}`,
	});

	// Helper function to handle V2 client/project filter response
	const handleV2FilterResponse = useCallback((filter, res) => {
		const { rows = [] } = res;

		if (filter === 'client') {
			const clientsArray = rows.clients || [];
			const clients = Array.isArray(clientsArray)
				? clientsArray.map(mapV2ClientToOption)
				: [];
			clients.unshift(allOption);
			setClientsList(clients);
		} else if (filter === 'project') {
			const projectsArray = rows.projects || [];
			const projects = Array.isArray(projectsArray)
				? projectsArray.map(mapV2ProjectToOption)
				: [];
			projects.unshift(allOption);
			setProjectsList(projects);
		}
	}, []);

	// Helper function to handle V1 client/project filter response
	const handleV1FilterResponse = useCallback((filter, res) => {
		const { rows = [] } = res;

		if (filter === 'client') {
			const clients = populateOptions(rows.clients);
			setClientsList(clients);
		} else if (filter === 'project') {
			const projects = populateOptions(rows.projects);
			setProjectsList(projects);
		}
	}, []);

	// Helper function to fetch client/project filters
	const fetchClientProjectFilter = useCallback(
		(filter) => {
			const isV2UI = getUIVersion() === 'new';

			if (isV2UI) {
				// Use V2 search API for client and project
				dispatch(getTripFilterListV2(filter))
					.then((res) => handleV2FilterResponse(filter, res))
					.catch((error) => {
						console.error(`Error fetching ${filter} filters:`, error);
						if (filter === 'client') {
							setClientsList([allOption]);
						} else if (filter === 'project') {
							setProjectsList([allOption]);
						}
					});
			} else {
				// V1 API for old UI
				dispatch(getTripFilterList(filter))
					.then((res) => handleV1FilterResponse(filter, res))
					.catch((error) => {
						console.error(`Error fetching ${filter} filters (V1):`, error);
					});
			}
		},
		[dispatch, handleV2FilterResponse, handleV1FilterResponse]
	);

	// Fetch filter lists — V1 (old UI) only; V2 uses the cascading hook above.
	useEffect(() => {
		if (getUIVersion() === 'new') return;
		fetchCityFilter();
		fetchVendorFilter();
		fetchClientProjectFilter('client');
		fetchClientProjectFilter('project');
	}, [dispatch, fetchCityFilter, fetchVendorFilter, fetchClientProjectFilter]);

	// Fetch business units - Always use V1 API even in new UI
	useEffect(() => {
		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 100,
				offset: 0,
				version: 'v1', // Explicitly use V1 API for business_unit
			})
		).then((res) => {
			const buOptions = (res?.rows || []).map((bu) => ({
				value: String(bu.id),
				label: bu.name,
			}));
			setBusinessUnitsList(buOptions);
		});
	}, [dispatch]);

	// Helper function to handle business unit IDs
	const getBusinessUnitIds = useCallback(() => {
		const buIds = paginationConfig?.business_unit_ids;
		let result = [];

		if (Array.isArray(buIds)) {
			result = buIds;
		} else if (buIds) {
			result = [buIds];
		}

		return result.map((businessUnitId) => String(businessUnitId).trim()).filter(Boolean);
	}, [paginationConfig?.business_unit_ids]);

	// Calculate business unit filter value
	useEffect(() => {
		const buIds = getBusinessUnitIds();
		if (buIds.length === 0) {
			setBusinessUnitFilterValue('');
			return;
		}

		if (!Array.isArray(businessUnitsList) || businessUnitsList.length === 0) {
			setBusinessUnitFilterValue('Business Unit');
			return;
		}

		const buNames = buIds
			.map((id) => {
				const businessUnit = businessUnitsList.find((item) => item.value === id);
				return businessUnit ? businessUnit.label : id;
			})
			.filter(Boolean);

		setBusinessUnitFilterValue(buNames.length > 0 ? 'Business Unit' : '');
	}, [businessUnitsList, getBusinessUnitIds]);

	// Helper function to load date range from localStorage
	const loadDateRangeFromStorage = (dateRangeKey) => {
		try {
			const storedDateRange = localStorage.getItem(dateRangeKey);
			if (!storedDateRange) return null;

			const data = JSON.parse(storedDateRange);
			const isExpired = Date.now() - data.timestamp > 24 * 60 * 60 * 1000;
			if (isExpired) {
				localStorage.removeItem(dateRangeKey);
				return null;
			}
			return data;
		} catch (error) {
			console.error('Error loading date range:', error);
			return null;
		}
	};

	// Helper function to load filters from localStorage
	const loadFiltersFromStorage = (filterStorageKey) => {
		try {
			const storedFilters = localStorage.getItem(filterStorageKey);
			if (!storedFilters) return null;

			const data = JSON.parse(storedFilters);
			const isExpired = Date.now() - data.timestamp > 24 * 60 * 60 * 1000;
			if (isExpired) {
				localStorage.removeItem(filterStorageKey);
				return null;
			}
			const { timestamp, ...filterData } = data;
			return filterData;
		} catch (error) {
			console.error('Error loading filters:', error);
			return null;
		}
	};

	// Helper function to apply date range to state
	const applyDateRangeToState = (dateRange) => {
		if (
			!dateRange?.from_date_moment ||
			!dateRange?.to_date_moment ||
			paginationConfig.from_date ||
			paginationConfig.to_date
		) {
			return;
		}

		const fromMoment =
			typeof dateRange.from_date_moment === 'string'
				? moment(dateRange.from_date_moment)
				: dateRange.from_date_moment;
		const toMoment =
			typeof dateRange.to_date_moment === 'string'
				? moment(dateRange.to_date_moment)
				: dateRange.to_date_moment;

		const fromDate = formatDateForRangePicker(fromMoment);
		const toDate = formatDateForRangePicker(toMoment);
		if (fromDate && toDate && fromDate.isValid() && toDate.isValid()) {
			setRangePickerValue([fromDate, toDate]);
			const apiFromDate = formatDateForAPI(fromDate);
			const apiToDate = formatEndDateForAPI(toDate);
			setPaginationConfig((prev) => ({
				...prev,
				from_date: apiFromDate,
				to_date: apiToDate,
			}));
		}
	};

	// Helper function to apply filters to state
	const applyFiltersToState = (filters) => {
		if (!filters) return;

		const filtersToApply = {};
		Object.keys(filters).forEach((key) => {
			if (paginationConfig[key] === undefined) {
				filtersToApply[key] = filters[key];
			}
		});

		if (Object.keys(filtersToApply).length > 0) {
			setPaginationConfig((prev) => ({
				...prev,
				...filtersToApply,
			}));
		}
	};

	// Load persisted date range and filters on component mount
	useEffect(() => {
		const dateRangeKey = `adhoc_request_${filterKey.toLowerCase()}_date_range`;
		const filterStorageKey = `adhoc_request_${filterKey.toLowerCase()}_filters`;

		const dateRange = loadDateRangeFromStorage(dateRangeKey);
		const filters = loadFiltersFromStorage(filterStorageKey);

		applyDateRangeToState(dateRange);
		applyFiltersToState(filters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Sync RangePicker UI with URL/state-provided dates
	useEffect(() => {
		if (paginationConfig?.from_date && paginationConfig?.to_date) {
			const fromUtc = moment.utc(paginationConfig.from_date, 'MM/DD/YYYY HH:mm:ss');
			const toUtc = moment.utc(paginationConfig.to_date, 'MM/DD/YYYY HH:mm:ss');
			if (fromUtc.isValid() && toUtc.isValid()) {
				const fromLocal = fromUtc.add(330, 'minutes');
				const toLocal = toUtc.add(330, 'minutes');
				setRangePickerValue([fromLocal, toLocal]);
			}
		}
	}, [paginationConfig?.from_date, paginationConfig?.to_date]);

	// Filter clear functions
	const clearCityFilter = () => {
		setPaginationConfig((prev) => ({
			...prev,
			city: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearClientFilter = () => {
		setPaginationConfig((prev) => ({
			...prev,
			client_id: undefined,
			client_name: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearProjectFilter = () => {
		setPaginationConfig((prev) => ({
			...prev,
			project_id: undefined,
			project_location_code: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearVendorFilter = () => {
		setPaginationConfig((prev) => ({
			...prev,
			vendor_id: undefined,
			vendor_name: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearTripStatusFilter = () => {
		setPaginationConfig((prev) => ({
			...prev,
			trip_status: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearBusinessUnitFilter = () => {
		setPaginationConfig((prev) => ({
			...prev,
			business_unit_ids: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	// Export functionality
	const createExport = useCallback(
		(toDate, fromDate) => {
			const isV2UI = getUIVersion() === 'new';
			const tab =
				// eslint-disable-next-line no-nested-ternary
				filterKey === 'PENDING_APPROVALS'
					? 'pending_approvals'
					: filterKey === 'PENDING_TRIPS'
					? 'pending_trips'
					: 'all';

			// Prepare export parameters with all active filters
			const exportParams = {
				tab,
				...(fromDate && { from_date: fromDate }),
				...(toDate && { to_date: toDate }),
				...(paginationConfig?.city && { city: paginationConfig.city }),
				...(paginationConfig?.client_id && { client_id: paginationConfig.client_id }),
				...(paginationConfig?.project_id && { project_id: paginationConfig.project_id }),
				...(paginationConfig?.vendor_id && { vendor_id: paginationConfig.vendor_id }),
				...(paginationConfig?.trip_status && { trip_status: paginationConfig.trip_status }),
				...(paginationConfig?.business_unit_ids && {
					business_unit_ids: paginationConfig.business_unit_ids,
				}),
				...(paginationConfig?.query_str && { query_str: paginationConfig.query_str }),
			};

			// Use V2 API for new UI, V1 for old UI
			const exportAction = isV2UI ? exportAdhocRequestsV2 : exportAdhocRequests;

			dispatch(exportAction(exportParams))
				.then((res) => {
					if (res?.download_url) {
						window.open(res.download_url);
					}
				})
				.catch((error) => {
					console.error('Export error:', error);
				});
		},
		[dispatch, filterKey, paginationConfig]
	);

	const handleExportClick = useCallback(() => {
		const toDate = paginationConfig?.to_date;
		const fromDate = paginationConfig?.from_date;

		if (!toDate || !fromDate || toDate === undefined || fromDate === undefined) {
			setExportWarningModal(true);
		} else {
			const diff = moment(toDate).diff(moment(fromDate), 'days');
			if (diff > 365) {
				setExportWarningModal(true);
			} else {
				createExport(toDate, fromDate);
			}
		}
	}, [paginationConfig?.to_date, paginationConfig?.from_date, createExport]);

	const handleExportWarningModalConfirm = useCallback(() => {
		const toDate = moment(new Date()).format(DATE_FORMAT_WITH_TIME);
		const fromDate = moment(toDate).subtract(60, 'days').format(DATE_FORMAT_WITH_TIME);

		setExportWarningModal(false);
		createExport(toDate, fromDate);
	}, [createExport]);

	const handleExportWarningModalCancel = useCallback(() => {
		setExportWarningModal(false);
	}, []);

	// Handle search
	const handleSearch = useCallback(
		(val) => {
			const trimmedVal = val?.trim() || '';
			setSearchValue(trimmedVal);
			const preservedFrom =
				paginationConfig?.from_date ||
				(rangePickerValue?.[0] ? formatDateForAPI(rangePickerValue[0]) : undefined);
			const preservedTo =
				paginationConfig?.to_date ||
				(rangePickerValue?.[1] ? formatEndDateForAPI(rangePickerValue[1]) : undefined);

			const newConfig = {
				...initialPaginateConfig,
				search_type: 'adhoc_request',
				fetch_filters: false,
				offset: 0,
				currentPage: 1,
				...(preservedFrom ? { from_date: preservedFrom } : {}),
				...(preservedTo ? { to_date: preservedTo } : {}),
			};

			// Only include query_str if it has a valid value
			if (trimmedVal) {
				newConfig.query_str = trimmedVal;
			}

			setPaginationConfig(newConfig);
		},
		[
			paginationConfig?.from_date,
			paginationConfig?.to_date,
			rangePickerValue,
			setPaginationConfig,
		]
	);

	// Handle date range picker change
	const handleDateRangeChange = useCallback(
		(val) => {
			setRangePickerValue(val);
			const dateRangeKey = `adhoc_request_${filterKey.toLowerCase()}_date_range`;
			const filterStorageKey = `adhoc_request_${filterKey.toLowerCase()}_filters`;

			if (val) {
				const fromDate = formatDateForAPI(val[0]);
				const toDate = formatEndDateForAPI(val[1]);
				const newConfig = {
					...paginationConfig,
					from_date: fromDate,
					to_date: toDate,
					offset: 0,
					currentPage: 1,
				};
				setPaginationConfig(newConfig);

				// Save date range
				try {
					const dateRangeData = {
						from_date: fromDate,
						to_date: toDate,
						from_date_moment: val[0].toISOString(),
						to_date_moment: val[1].toISOString(),
						timestamp: Date.now(),
					};
					localStorage.setItem(dateRangeKey, JSON.stringify(dateRangeData));

					// Save filters
					const { from_date, to_date, ...otherFilters } = newConfig;
					const filterData = {
						...otherFilters,
						timestamp: Date.now(),
					};
					localStorage.setItem(filterStorageKey, JSON.stringify(filterData));
				} catch (error) {
					console.error('Error saving filters:', error);
				}
			} else {
				const newConfig = {
					...paginationConfig,
					from_date: false,
					to_date: false,
					offset: 0,
					currentPage: 1,
				};
				setPaginationConfig(newConfig);

				// Clear storage
				try {
					localStorage.removeItem(dateRangeKey);
					const { from_date, to_date, ...otherFilters } = newConfig;
					const filterData = {
						...otherFilters,
						timestamp: Date.now(),
					};
					localStorage.setItem(filterStorageKey, JSON.stringify(filterData));
				} catch (error) {
					console.error('Error clearing filters:', error);
				}
			}
		},
		[paginationConfig, setPaginationConfig, filterKey]
	);

	// Filter values for display
	const cityFilterValue = paginationConfig?.city ? 'City' : '';
	const client = paginationConfig?.client_id ? 'Client' : '';
	const project = paginationConfig?.project_id ? 'Project' : '';
	const vendorValue = paginationConfig?.vendor_id ? 'Vendor' : '';
	const tripStatus = paginationConfig?.trip_status ? 'Status' : '';

	return (
		<>
			<ResponsiveFilterContainer
				background="#FFFFFF"
				borderRadius="8px"
				p="24px"
				boxShadow="0 2px 8px rgba(0,0,0,0.04)"
				mb={4}
			>
				<ResponsiveFlexContainer
					justifyContent="space-between"
					alignItems="center"
					mt={3}
					flexWrap="nowrap"
				>
					<ResponsiveSearchBox>
						<LtSearchBar
							placeholder="Search"
							enterButton="Search"
							value={searchValue}
							allowClear
							onChange={(e) => setSearchValue(e.target.value)}
							onSearch={handleSearch}
						/>
					</ResponsiveSearchBox>
					<ResponsiveFiltersBox display="flex" alignItems="center">
						<ResponsiveFilterTagsContainer>
							{cityFilterValue && (
								<FilterTag onClose={clearCityFilter}>{cityFilterValue}</FilterTag>
							)}
							{businessUnitFilterValue && (
								<FilterTag onClose={clearBusinessUnitFilter}>
									{businessUnitFilterValue}
								</FilterTag>
							)}
							{client && <FilterTag onClose={clearClientFilter}>{client}</FilterTag>}
							{project && (
								<FilterTag onClose={clearProjectFilter}>{project}</FilterTag>
							)}
							{vendorValue && (
								<FilterTag onClose={clearVendorFilter}>{vendorValue}</FilterTag>
							)}
							{tripStatus && (
								<FilterTag onClose={clearTripStatusFilter}>{tripStatus}</FilterTag>
							)}
						</ResponsiveFilterTagsContainer>
						<FilterAtnButton onClick={() => setOpen(true)}>FILTERS</FilterAtnButton>
					</ResponsiveFiltersBox>
					<ResponsiveDateBox>
						<ResponsiveDateLabel whiteSpace="nowrap" marginRight="1">
							Select Date :
						</ResponsiveDateLabel>
						<ResponsiveDatePicker
							name="date_filter"
							allowClear
							value={rangePickerValue}
							format={['DD MMM YY', 'YYYY-MM-DD']}
							onChange={handleDateRangeChange}
						/>
						<ACL action="create" module="trip_adhoc_request">
							{showCreateButton && (
								<ButtonWrapper
									icons={<PlusOutlined />}
									onClick={() => navigate('/trips/adhoc-request/create')}
								>
									CREATE
								</ButtonWrapper>
							)}
						</ACL>
						<ButtonWrapper onClick={handleExportClick} icons={<DownloadOutlined />}>
							EXPORT
						</ButtonWrapper>
					</ResponsiveDateBox>
				</ResponsiveFlexContainer>
			</ResponsiveFilterContainer>
			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setOpen(false)}
				open={open}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px">Business Unit</Text>
						<Select
							mode="multiple"
							showSearch
							style={{ width: '100%' }}
							name="business_unit_ids"
							value={getBusinessUnitIds()}
							onChange={(val) => {
								setPaginationConfig((prev) => ({
									...prev,
									business_unit_ids: val,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={businessUnitsList}
							maxTagCount="responsive"
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">City</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="city"
							value={paginationConfig?.city}
							onChange={(val) =>
								setPaginationConfig((prev) => ({
									...prev,
									city: val,
									// reset dependent Project when City changes
									project_id: undefined,
									offset: 0,
									currentPage: 1,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={citiesList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Client</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="client_name"
							value={paginationConfig?.client_id}
							onChange={(val) => {
								const selectedOption = clientsList?.find(
									(option) => option.value === val
								);
								setPaginationConfig((prev) => ({
									...prev,
									client_id: val,
									client_name: selectedOption?.label || undefined,
									// reset dependent Project when Client changes
									project_id: undefined,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={clientsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Project</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="project_location_code"
							value={paginationConfig?.project_id}
							onChange={(val) => {
								const selectedOption = projectsList?.find(
									(option) => option.value === val
								);
								setPaginationConfig((prev) => ({
									...prev,
									project_id: val,
									project_location_code: selectedOption?.label || undefined,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={projectsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Vendor</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="vendor_name"
							value={paginationConfig?.vendor_id}
							onChange={(val) => {
								const selectedOption = vendorsList?.find(
									(option) => option.value === val
								);
								setPaginationConfig((prev) => ({
									...prev,
									vendor_id: val,
									vendor_name: selectedOption?.label || undefined,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={vendorsList}
						/>
					</Box>
					<Box>
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() => {
								const dateRangeKey = `adhoc_request_${filterKey.toLowerCase()}_date_range`;
								const filterStorageKey = `adhoc_request_${filterKey.toLowerCase()}_filters`;
								try {
									localStorage.removeItem(dateRangeKey);
									localStorage.removeItem(filterStorageKey);
								} catch (error) {
									console.error('Error clearing filters:', error);
								}
								window.location = `${window.origin}/trips/adhoc-request`;
							}}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>
			<CustomModal
				open={exportWarningModal}
				onCancel={() => setExportWarningModal(false)}
				footer={null}
				closable={false}
				style={{ top: 200 }}
				width={488}
			>
				<ModalContainer>
					<UpperContent>
						<WarningIcon>
							<svg
								width="36"
								height="32"
								viewBox="0 0 36 32"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M1.76885 31.75C1.44802 31.75 1.15635 31.6701 0.893854 31.5103C0.631354 31.3504 0.427188 31.1387 0.281354 30.875C0.135521 30.6113 0.0556043 30.3267 0.0416043 30.021C0.0276043 29.7153 0.107521 29.4167 0.281354 29.125L16.4689 1.125C16.6439 0.833333 16.8702 0.614583 17.1479 0.46875C17.4255 0.322917 17.7096 0.25 18.0001 0.25C18.2906 0.25 18.5753 0.322917 18.8541 0.46875C19.1329 0.614583 19.3587 0.833333 19.5314 1.125L35.7189 29.125C35.8939 29.4167 35.9744 29.7159 35.9604 30.0228C35.9464 30.3296 35.8659 30.6137 35.7189 30.875C35.5719 31.1363 35.3677 31.3481 35.1064 31.5103C34.845 31.6724 34.5534 31.7523 34.2314 31.75H1.76885ZM4.7876 28.25H31.2126L18.0001 5.5L4.7876 28.25ZM18.0001 26.5C18.4959 26.5 18.9119 26.332 19.2479 25.996C19.5839 25.66 19.7513 25.2447 19.7501 24.75C19.7489 24.2553 19.5809 23.84 19.2461 23.504C18.9113 23.168 18.4959 23 18.0001 23C17.5043 23 17.0889 23.168 16.7541 23.504C16.4193 23.84 16.2513 24.2553 16.2501 24.75C16.2489 25.2447 16.4169 25.6606 16.7541 25.9977C17.0913 26.3349 17.5066 26.5023 18.0001 26.5ZM18.0001 21.25C18.4959 21.25 18.9119 21.082 19.2479 20.746C19.5839 20.41 19.7513 19.9947 19.7501 19.5V14.25C19.7501 13.7542 19.5821 13.3388 19.2461 13.004C18.9101 12.6692 18.4948 12.5012 18.0001 12.5C17.5054 12.4988 17.0901 12.6668 16.7541 13.004C16.4181 13.3412 16.2501 13.7565 16.2501 14.25V19.5C16.2501 19.9958 16.4181 20.4118 16.7541 20.7478C17.0901 21.0838 17.5054 21.2512 18.0001 21.25Z"
									fill="#FF3B30"
								/>
							</svg>
						</WarningIcon>
						<TextContainer>
							<TitleContainer>
								<WarningText>
									You are trying to export a large volume of data. Limit your
									selection to a shorter date range
								</WarningText>
							</TitleContainer>
							<QuestionContainer>
								<QuestionText>
									Do you want to proceed with last 60 days data
								</QuestionText>
							</QuestionContainer>
						</TextContainer>
					</UpperContent>
					<ActionButtons>
						<NoButton onClick={handleExportWarningModalCancel}>No</NoButton>
						<YesButton type="primary" onClick={handleExportWarningModalConfirm}>
							Yes
						</YesButton>
					</ActionButtons>
				</ModalContainer>
			</CustomModal>
		</>
	);
}

AdhocRequestFilters.propTypes = {
	paginationConfig: PropTypes.object.isRequired,
	setPaginationConfig: PropTypes.func.isRequired,
	filterKey: PropTypes.string.isRequired,
	showCreateButton: PropTypes.bool,
};

export default AdhocRequestFilters;
